import {
  AvailableSubscriptionPackagesQuery,
  AvailableSubscriptionPackagesQueryVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useEffect, useMemo } from "react";

import { useCreditOffer } from "../../../hooks/useCreditOffer";
import useExperiment from "../../../hooks/useExperiment";

const SUBSCRIPTION_PACKAGES_QUERY = gql`
  query AvailableSubscriptionPackages {
    availableSubscriptionPackages {
      uid
      amountCents
      numCredits
      creditValue
      savingsPercentage
      savingsCents
      classPerWeekAvg
      numFreeCredits
    }
  }
`;

// Experiment constants
const LOWER_COST_TIER_EXPERIMENT_NAME = "subscription-lower-cost-tier";
const LOWER_COST_TIER_PACKAGE_COST_CENTS = 2000; // $20 tier

export function useSubscriptionPlans() {
  const { data, loading, error } = useQueryWithPreviousData<
    AvailableSubscriptionPackagesQuery,
    AvailableSubscriptionPackagesQueryVariables
  >(SUBSCRIPTION_PACKAGES_QUERY);

  const { creditOffer } = useCreditOffer();
  const { variant: experimentVariant, trigger: triggerExperiment } =
    useExperiment(LOWER_COST_TIER_EXPERIMENT_NAME);

  // Filter plans based on experiment variant
  const subscriptionPlans = useMemo(() => {
    const allPlans = data?.availableSubscriptionPackages ?? [];

    if (experimentVariant === "treatment") {
      // Treatment: Show both $20 and $40 tiers
      return allPlans;
    } else {
      // Control or holdout: Hide $20 tier, show $40 tier
      return allPlans.filter(
        plan => plan.amountCents !== LOWER_COST_TIER_PACKAGE_COST_CENTS,
      );
    }
  }, [data?.availableSubscriptionPackages, experimentVariant]);

  // Calculate the trial plan (lowest cost plan available)
  const trialPlan =
    subscriptionPlans.length > 0
      ? subscriptionPlans.reduce((lowest, plan) =>
          plan.amountCents < lowest.amountCents ? plan : lowest,
        )
      : null;

  // Calculate the lowest cost plan eligible for the current credit offer
  // Used for credit offer landing pages
  const creditOfferEligiblePlan = useMemo(() => {
    const eligiblePlanCredits = creditOffer?.eligiblePlans;

    if (!eligiblePlanCredits || eligiblePlanCredits.length === 0) {
      return null;
    }

    const eligiblePlans = subscriptionPlans.filter(plan =>
      eligiblePlanCredits.includes(plan.numCredits),
    );

    return eligiblePlans.length > 0
      ? eligiblePlans.reduce((lowest, plan) =>
          plan.amountCents < lowest.amountCents ? plan : lowest,
        )
      : null;
  }, [creditOffer?.eligiblePlans, subscriptionPlans]);

  // Trigger the experiment when we're actually showing subscription plans
  useEffect(() => {
    if (!loading && subscriptionPlans.length > 0) {
      triggerExperiment();
    }
  }, [loading, subscriptionPlans.length, triggerExperiment]);

  return {
    subscriptionPlans,
    trialPlan,
    creditOfferEligiblePlan,
    loading,
    error,
  };
}
